/**
 * Admin Detail Pages - Refactored
 * Detail pages using the new admin component system
 */

import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Users, Palette, BookOpen, Image, Flag, FileText,
  Mail, Calendar, Shield, Ban, CheckCircle, XCircle,
  Eye, Edit, Trash2, AlertTriangle, Clock, Heart,
  MessageSquare, BarChart3, ExternalLink, RefreshCw
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  AdminPageShell,
  AdminCardShell,
  AdminSection,
  AdminDetailCard,
  AdminDetailField,
  AdminDetailGrid,
  AdminLoadingState,
  AdminErrorState,
  AdminEmptyState,
  useAdminDetail,
  useAdminMutation,
  adminPost,
  adminPatch,
} from '../admin/components';

// ===========================
// ADMIN USER DETAIL
// ===========================
export const AdminUserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState(false);

  const {
    data: user,
    loading,
    error,
    isAuthError,
    refresh,
    hasData,
  } = useAdminDetail('users', id);

  const handleBan = async () => {
    const reason = prompt('Enter ban reason:');
    if (!reason) return;
    
    setActionLoading(true);
    const result = await adminPost(`/admin/users/${id}/ban`, { reason });
    setActionLoading(false);
    
    if (result.ok) {
      toast.success('User banned');
      refresh();
    } else {
      toast.error(result.error);
    }
  };

  const handleUnban = async () => {
    setActionLoading(true);
    const result = await adminPost(`/admin/users/${id}/unban`, {});
    setActionLoading(false);
    
    if (result.ok) {
      toast.success('User unbanned');
      refresh();
    } else {
      toast.error(result.error);
    }
  };

  const handleChangeRole = async (newRole) => {
    if (!confirm(`Change user role to ${newRole}?`)) return;
    
    setActionLoading(true);
    const result = await adminPatch(`/admin/users/${id}/role`, { new_role: newRole });
    setActionLoading(false);
    
    if (result.ok) {
      toast.success('Role updated');
      refresh();
    } else {
      toast.error(result.error);
    }
  };

  const headerActions = hasData && (
    <>
      {user.status === 'banned' ? (
        <Button
          onClick={handleUnban}
          disabled={actionLoading}
          variant="outline"
          size="sm"
          className="border-green-500 text-green-400 hover:bg-green-500/10"
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          Unban
        </Button>
      ) : (
        <Button
          onClick={handleBan}
          disabled={actionLoading}
          variant="outline"
          size="sm"
          className="border-red-500 text-red-400 hover:bg-red-500/10"
        >
          <Ban className="w-4 h-4 mr-2" />
          Ban User
        </Button>
      )}
    </>
  );

  return (
    <AdminPageShell
      title={hasData ? (user.display_name || user.email) : 'User Details'}
      subtitle={hasData ? user.email : undefined}
      icon={Users}
      headerColor="from-blue-600 to-cyan-600"
      backLink="/admin/users"
      backLabel="Back to Users"
      loading={loading}
      error={error}
      isAuthError={isAuthError}
      onRetry={refresh}
      headerActions={headerActions}
    >
      {hasData && (
        <div className="space-y-6">
          {/* Missing Profile Warning */}
          {user.profile_warning && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30" data-testid="missing-profile-warning">
              <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0" />
              <span className="text-yellow-300 text-sm">{user.profile_warning}</span>
            </div>
          )}
          
          {/* User Info Card */}
          <AdminDetailCard title="User Information" icon={Users}>
            <div className="flex items-start gap-6">
              {user.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-24 h-24 rounded-full object-cover" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-zinc-700 flex items-center justify-center">
                  <Users className="w-10 h-10 text-zinc-500" />
                </div>
              )}
              <AdminDetailGrid columns={2} className="flex-1">
                <AdminDetailField label="Email" value={user.email} icon={Mail} copyable />
                <AdminDetailField 
                  label="Role" 
                  value={
                    <Badge variant="outline" className={
                      user.role === 'admin' ? 'bg-red-500/20 text-red-400' :
                      user.role === 'creator' ? 'bg-purple-500/20 text-purple-400' :
                      'bg-zinc-500/20 text-zinc-400'
                    }>
                      {user.role || 'user'}
                    </Badge>
                  } 
                  icon={Shield} 
                />
                <AdminDetailField 
                  label="Status" 
                  value={
                    <Badge variant="outline" className={
                      user.status === 'banned' ? 'bg-red-500/20 text-red-400' :
                      user.status === 'suspended' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-green-500/20 text-green-400'
                    }>
                      {user.status || 'active'}
                    </Badge>
                  } 
                  icon={CheckCircle} 
                />
                <AdminDetailField 
                  label="Joined" 
                  value={user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'} 
                  icon={Calendar} 
                />
              </AdminDetailGrid>
            </div>
          </AdminDetailCard>

          {/* Actions Card */}
          <AdminDetailCard title="Admin Actions" icon={Shield}>
            <div className="flex gap-3 flex-wrap">
              <select
                value={user.role || 'basic_user'}
                onChange={(e) => handleChangeRole(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white"
                disabled={actionLoading}
              >
                <option value="basic_user">Basic User</option>
                <option value="creator">Creator</option>
                <option value="admin">Admin</option>
              </select>
              {user.role === 'creator' && user.user_id && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/admin/creators/${user.user_id}`)}
                >
                  View Creator Profile
                </Button>
              )}
            </div>
          </AdminDetailCard>

          {/* Activity Stats */}
          <AdminDetailCard title="Activity" icon={BarChart3}>
            <AdminDetailGrid columns={4}>
              <AdminDetailField label="Series" value={user.series_count || 0} />
              <AdminDetailField label="Artworks" value={user.artwork_count || 0} />
              <AdminDetailField label="Comments" value={user.comment_count || 0} />
              <AdminDetailField label="Reports Filed" value={user.report_count || 0} />
            </AdminDetailGrid>
          </AdminDetailCard>
        </div>
      )}
    </AdminPageShell>
  );
};

// ===========================
// ADMIN SERIES DETAIL
// ===========================
export const AdminSeriesDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState(false);

  const {
    data: series,
    loading,
    error,
    isAuthError,
    refresh,
    hasData,
  } = useAdminDetail('series', id);

  const handleTakeDown = async () => {
    const reason = prompt('Enter takedown reason:');
    if (!reason) return;
    
    setActionLoading(true);
    const result = await adminPost(`/admin/series/${id}/takedown`, { reason });
    setActionLoading(false);
    
    if (result.ok) {
      toast.success('Series taken down');
      refresh();
    } else {
      toast.error(result.error);
    }
  };

  const handleRestore = async () => {
    setActionLoading(true);
    const result = await adminPost(`/admin/series/${id}/restore`, {});
    setActionLoading(false);
    
    if (result.ok) {
      toast.success('Series restored');
      refresh();
    } else {
      toast.error(result.error);
    }
  };

  const headerActions = hasData && (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.open(`/comics/${id}`, '_blank')}
        className="border-white/30 text-white hover:bg-white/10"
      >
        <ExternalLink className="w-4 h-4 mr-2" />
        View on Site
      </Button>
      {series.status === 'taken_down' ? (
        <Button
          onClick={handleRestore}
          disabled={actionLoading}
          size="sm"
          className="bg-green-600 hover:bg-green-700"
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          Restore
        </Button>
      ) : (
        <Button
          onClick={handleTakeDown}
          disabled={actionLoading}
          size="sm"
          className="bg-red-600 hover:bg-red-700"
        >
          <Ban className="w-4 h-4 mr-2" />
          Take Down
        </Button>
      )}
    </>
  );

  return (
    <AdminPageShell
      title={hasData ? series.title : 'Series Details'}
      subtitle={hasData ? `by ${series.creator_name || 'Unknown'}` : undefined}
      icon={BookOpen}
      headerColor="from-green-600 to-teal-600"
      backLink="/admin/series"
      backLabel="Back to Series"
      loading={loading}
      error={error}
      isAuthError={isAuthError}
      onRetry={refresh}
      headerActions={headerActions}
    >
      {hasData && (
        <div className="space-y-6">
          {/* Series Info */}
          <AdminDetailCard title="Series Information" icon={BookOpen}>
            <div className="flex items-start gap-6">
              {series.cover_image_url ? (
                <img src={series.cover_image_url} alt="" className="w-32 h-44 rounded object-cover" />
              ) : (
                <div className="w-32 h-44 rounded bg-zinc-700 flex items-center justify-center">
                  <BookOpen className="w-10 h-10 text-zinc-500" />
                </div>
              )}
              <div className="flex-1">
                <AdminDetailGrid columns={2}>
                  <AdminDetailField label="Title" value={series.title} />
                  <AdminDetailField 
                    label="Status" 
                    value={
                      <Badge variant="outline" className={
                        series.status === 'published' ? 'bg-green-500/20 text-green-400' :
                        series.status === 'taken_down' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }>
                        {series.status || 'draft'}
                      </Badge>
                    }
                  />
                  <AdminDetailField label="Creator" value={series.creator_name} />
                  <AdminDetailField label="Genre" value={series.genre?.join(', ') || '—'} />
                  <AdminDetailField label="Chapters" value={series.chapter_count || 0} />
                  <AdminDetailField label="Created" value={series.created_at ? new Date(series.created_at).toLocaleDateString() : '—'} />
                </AdminDetailGrid>
                {series.description && (
                  <div className="mt-4">
                    <h4 className="text-sm text-zinc-400 mb-1">Description</h4>
                    <p className="text-zinc-300">{series.description}</p>
                  </div>
                )}
              </div>
            </div>
          </AdminDetailCard>

          {/* Stats */}
          <AdminDetailCard title="Statistics" icon={BarChart3}>
            <AdminDetailGrid columns={4}>
              <AdminDetailField label="Views" value={series.views || 0} icon={Eye} />
              <AdminDetailField label="Likes" value={series.likes || 0} icon={Heart} />
              <AdminDetailField label="Comments" value={series.comment_count || 0} icon={MessageSquare} />
              <AdminDetailField label="Subscribers" value={series.subscriber_count || 0} icon={Users} />
            </AdminDetailGrid>
          </AdminDetailCard>
        </div>
      )}
    </AdminPageShell>
  );
};

// ===========================
// ADMIN ARTWORK DETAIL
// ===========================
export const AdminArtworkDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState(false);

  const {
    data: artwork,
    loading,
    error,
    isAuthError,
    refresh,
    hasData,
  } = useAdminDetail('artworks', id);

  const handleTakeDown = async () => {
    const reason = prompt('Enter takedown reason:');
    if (!reason) return;
    
    setActionLoading(true);
    const result = await adminPost(`/admin/artworks/${id}/takedown`, { reason });
    setActionLoading(false);
    
    if (result.ok) {
      toast.success('Artwork taken down');
      refresh();
    } else {
      toast.error(result.error);
    }
  };

  return (
    <AdminPageShell
      title={hasData ? (artwork.title || 'Untitled') : 'Artwork Details'}
      subtitle={hasData ? `by ${artwork.artist_name || 'Unknown'}` : undefined}
      icon={Image}
      headerColor="from-pink-600 to-rose-600"
      backLink="/admin/artworks"
      backLabel="Back to Artworks"
      loading={loading}
      error={error}
      isAuthError={isAuthError}
      onRetry={refresh}
      headerActions={hasData && artwork.status !== 'taken_down' && (
        <Button
          onClick={handleTakeDown}
          disabled={actionLoading}
          size="sm"
          className="bg-red-600 hover:bg-red-700"
        >
          <Ban className="w-4 h-4 mr-2" />
          Take Down
        </Button>
      )}
    >
      {hasData && (
        <div className="space-y-6">
          {/* Artwork Preview */}
          <AdminDetailCard title="Artwork Preview" icon={Image}>
            <div className="flex items-start gap-6">
              {(artwork.image_url || artwork.thumbnail_url) ? (
                <img 
                  src={artwork.image_url || artwork.thumbnail_url} 
                  alt="" 
                  className="max-w-md max-h-96 rounded object-contain bg-zinc-800" 
                />
              ) : (
                <div className="w-64 h-64 rounded bg-zinc-700 flex items-center justify-center">
                  <Image className="w-16 h-16 text-zinc-500" />
                </div>
              )}
              <AdminDetailGrid columns={1} className="flex-1">
                <AdminDetailField label="Title" value={artwork.title || 'Untitled'} />
                <AdminDetailField label="Artist" value={artwork.artist_name} />
                <AdminDetailField 
                  label="Status" 
                  value={
                    <Badge variant="outline" className={
                      artwork.status === 'taken_down' ? 'bg-red-500/20 text-red-400' :
                      'bg-green-500/20 text-green-400'
                    }>
                      {artwork.status || 'active'}
                    </Badge>
                  }
                />
                <AdminDetailField label="Uploaded" value={artwork.created_at ? new Date(artwork.created_at).toLocaleDateString() : '—'} />
              </AdminDetailGrid>
            </div>
          </AdminDetailCard>

          {/* Stats */}
          <AdminDetailCard title="Engagement" icon={BarChart3}>
            <AdminDetailGrid columns={3}>
              <AdminDetailField label="Views" value={artwork.views || 0} icon={Eye} />
              <AdminDetailField label="Likes" value={artwork.likes || 0} icon={Heart} />
              <AdminDetailField label="Comments" value={artwork.comment_count || 0} icon={MessageSquare} />
            </AdminDetailGrid>
          </AdminDetailCard>
        </div>
      )}
    </AdminPageShell>
  );
};

// ===========================
// ADMIN REPORT DETAIL
// ===========================
export const AdminReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState(false);

  const {
    data: report,
    loading,
    error,
    isAuthError,
    refresh,
    hasData,
  } = useAdminDetail('reports', id);

  const handleResolve = async (action) => {
    const reason = action === 'dismiss' ? '' : prompt('Enter action reason (optional):');
    
    setActionLoading(true);
    const result = await adminPost(`/admin/reports/${id}/resolve`, { action, reason });
    setActionLoading(false);
    
    if (result.ok) {
      toast.success(`Report ${action}ed`);
      navigate('/admin/reports');
    } else {
      toast.error(result.error);
    }
  };

  const headerActions = hasData && report.status === 'pending' && (
    <>
      <Button
        onClick={() => handleResolve('action')}
        disabled={actionLoading}
        size="sm"
        className="bg-red-600 hover:bg-red-700"
      >
        <AlertTriangle className="w-4 h-4 mr-2" />
        Take Action
      </Button>
      <Button
        onClick={() => handleResolve('dismiss')}
        disabled={actionLoading}
        variant="outline"
        size="sm"
        className="border-zinc-500"
      >
        <XCircle className="w-4 h-4 mr-2" />
        Dismiss
      </Button>
    </>
  );

  return (
    <AdminPageShell
      title="Report Details"
      subtitle={hasData ? `Report #${id.slice(0, 8)}` : undefined}
      icon={Flag}
      headerColor="from-red-600 to-orange-600"
      backLink="/admin/reports"
      backLabel="Back to Reports"
      loading={loading}
      error={error}
      isAuthError={isAuthError}
      onRetry={refresh}
      headerActions={headerActions}
    >
      {hasData && (
        <div className="space-y-6">
          {/* Report Info */}
          <AdminDetailCard title="Report Information" icon={Flag}>
            <AdminDetailGrid columns={2}>
              <AdminDetailField 
                label="Status" 
                value={
                  <Badge variant="outline" className={
                    report.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                    report.status === 'actioned' ? 'bg-green-500/20 text-green-400' :
                    'bg-zinc-500/20 text-zinc-400'
                  }>
                    {report.status}
                  </Badge>
                }
              />
              <AdminDetailField label="Reported At" value={report.created_at ? new Date(report.created_at).toLocaleString() : '—'} icon={Calendar} />
              <AdminDetailField label="Reporter" value={report.reporter_name || report.reporter_email} icon={Users} />
              <AdminDetailField label="Content Type" value={report.content_type} />
            </AdminDetailGrid>
          </AdminDetailCard>

          {/* Report Reason */}
          <AdminDetailCard title="Report Reason" icon={AlertTriangle}>
            <p className="text-zinc-300">{report.reason}</p>
            {report.additional_info && (
              <p className="text-zinc-500 mt-2">{report.additional_info}</p>
            )}
          </AdminDetailCard>

          {/* Reported Content */}
          <AdminDetailCard title="Reported Content" icon={FileText}>
            <div className="space-y-2">
              <AdminDetailField label="Title" value={report.content_title || 'Unknown'} />
              <AdminDetailField label="Content ID" value={report.content_id} copyable />
              {report.content_url && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(report.content_url, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Content
                </Button>
              )}
            </div>
          </AdminDetailCard>
        </div>
      )}
    </AdminPageShell>
  );
};

// ===========================
// ADMIN NOVEL DETAIL
// ===========================
export const AdminNovelDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState(false);

  const {
    data: novel,
    loading,
    error,
    isAuthError,
    refresh,
    hasData,
  } = useAdminDetail('novels', id);

  const handleTakeDown = async () => {
    const reason = prompt('Enter takedown reason:');
    if (!reason) return;
    
    setActionLoading(true);
    const result = await adminPost(`/admin/novels/${id}/takedown`, { reason });
    setActionLoading(false);
    
    if (result.ok) {
      toast.success('Novel taken down');
      refresh();
    } else {
      toast.error(result.error);
    }
  };

  return (
    <AdminPageShell
      title={hasData ? novel.title : 'Novel Details'}
      subtitle={hasData ? `by ${novel.author_name || 'Unknown'}` : undefined}
      icon={FileText}
      headerColor="from-indigo-600 to-purple-600"
      backLink="/admin/novels"
      backLabel="Back to Novels"
      loading={loading}
      error={error}
      isAuthError={isAuthError}
      onRetry={refresh}
      headerActions={hasData && novel.status !== 'taken_down' && (
        <Button
          onClick={handleTakeDown}
          disabled={actionLoading}
          size="sm"
          className="bg-red-600 hover:bg-red-700"
        >
          <Ban className="w-4 h-4 mr-2" />
          Take Down
        </Button>
      )}
    >
      {hasData && (
        <div className="space-y-6">
          {/* Novel Info */}
          <AdminDetailCard title="Novel Information" icon={FileText}>
            <div className="flex items-start gap-6">
              {novel.cover_image_url ? (
                <img src={novel.cover_image_url} alt="" className="w-32 h-44 rounded object-cover" />
              ) : (
                <div className="w-32 h-44 rounded bg-zinc-700 flex items-center justify-center">
                  <FileText className="w-10 h-10 text-zinc-500" />
                </div>
              )}
              <AdminDetailGrid columns={2} className="flex-1">
                <AdminDetailField label="Title" value={novel.title} />
                <AdminDetailField 
                  label="Status" 
                  value={
                    <Badge variant="outline" className={
                      novel.status === 'published' ? 'bg-green-500/20 text-green-400' :
                      novel.status === 'taken_down' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }>
                      {novel.status || 'draft'}
                    </Badge>
                  }
                />
                <AdminDetailField label="Author" value={novel.author_name} />
                <AdminDetailField label="Genre" value={novel.genre?.join(', ') || '—'} />
                <AdminDetailField label="Chapters" value={novel.chapter_count || 0} />
                <AdminDetailField label="Word Count" value={novel.word_count?.toLocaleString() || '—'} />
              </AdminDetailGrid>
            </div>
          </AdminDetailCard>

          {/* Stats */}
          <AdminDetailCard title="Statistics" icon={BarChart3}>
            <AdminDetailGrid columns={4}>
              <AdminDetailField label="Views" value={novel.views || 0} icon={Eye} />
              <AdminDetailField label="Likes" value={novel.likes || 0} icon={Heart} />
              <AdminDetailField label="Comments" value={novel.comment_count || 0} icon={MessageSquare} />
              <AdminDetailField label="Subscribers" value={novel.subscriber_count || 0} icon={Users} />
            </AdminDetailGrid>
          </AdminDetailCard>
        </div>
      )}
    </AdminPageShell>
  );
};

export default {
  AdminUserDetail,
  AdminSeriesDetail,
  AdminArtworkDetail,
  AdminReportDetail,
  AdminNovelDetail,
};
